import { axiosInstance } from './axiosInstance';
import {
    CreateImageLayoutRequest,
    ImageLayoutResponse,
    ImageLayoutAttributes,
} from '../types/imageLayoutTypes';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

export const createImageLayout = createAsyncThunk<
    ImageLayoutAttributes,
    { imageProjectId: string; layout: CreateImageLayoutRequest },
    { rejectValue: string }
>('image/createImageLayout', async ({ imageProjectId, layout }, { rejectWithValue }) => {
    try {
        const data = {
            data: {
                type: 'image_layouts',
                attributes: {
                    slug: layout.slug,
                    order: layout.order,
                    canvas_color: layout.canvas_color,
                    gap_size: layout.gap_size,
                    inline_text: [],
                },
            },
        };
        const response = await axiosInstance.post<ImageLayoutResponse>(
            `/image_projects/${imageProjectId}/layouts`,
            data,
        );

        const transformedLayout = {
            ...response.data.data.attributes,
            id: response.data.data.id,
        };
        return transformedLayout;
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || error.message || 'Unknown error');
    }
});

export const updateImageLayout = createAsyncThunk<
    ImageLayoutAttributes,
    { imageProjectId: string; layoutId: string; layout: CreateImageLayoutRequest },
    { rejectValue: string }
>('image/updateImageLayout', async ({ imageProjectId, layoutId, layout }, thunkApi) => {
    try {
        const { slot_count, ...layoutWithoutSlotCount } = layout;

        if (
            layoutWithoutSlotCount.canvas_color &&
            layoutWithoutSlotCount.canvas_color.gradient === ''
        ) {
            layoutWithoutSlotCount.canvas_color = {
                ...layoutWithoutSlotCount.canvas_color,
                gradient:
                    layoutWithoutSlotCount.canvas_color.gradient === ''
                        ? 'none'
                        : layoutWithoutSlotCount.canvas_color.gradient,
            };
        }

        console.log('layoutWithoutSlotCount', layoutWithoutSlotCount);
        const data = {
            data: {
                id: layoutId,
                type: 'image_layouts',
                attributes: layoutWithoutSlotCount,
            },
        };
        const response = await axiosInstance.patch<ImageLayoutResponse>(
            `/image_projects/${imageProjectId}/layouts/${layoutId}`,
            data,
        );
        const transformedLayout = {
            id: response.data.data.id,
            slug: response.data.data.attributes.slug,
            order: response.data.data.attributes.order,
            slot_count: slot_count ?? 1,
            gap_size: response.data.data.attributes.gap_size,
            canvas_color: response.data.data.attributes.canvas_color,
            transformation: response.data.data.attributes.transformation,
            images: response.data.data.relationships.images.data,
            inline_text: response.data.data.attributes.inline_text,
        };
        console.log('transformedLayout', transformedLayout);
        return transformedLayout;
    } catch (error: any) {
        return thunkApi.rejectWithValue(
            error.response?.data?.message || error.message || 'Unknown error',
        );
    }
});

export const addImagesToLayout = createAsyncThunk<
    { layoutId: string; images: { id: string; meta: { order: number } }[] },
    { layoutId: string; images: { id: string; order: number }[] },
    { rejectValue: string }
>('image/addImagesToLayout', async ({ layoutId, images }, { rejectWithValue }) => {
    try {
        const data = {
            data: images.map((img, index) => ({
                type: 'images',
                id: img.id,
                meta: {
                    order: img.order,
                },
            })),
        };

        const res = await axiosInstance.post(
            `/image_layouts/${layoutId}/relationships/images`,
            data,
        );
        const transformedData = res.data.data.relationships.images.data;
        console.log('transformedData', transformedData);
        return {
            layoutId: layoutId,
            images: transformedData,
        };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || error.message || 'Unknown error');
    }
});

export const changeImageOrder = createAsyncThunk<
    { layoutId: string; images: { id: string; meta: { order: number } }[] },
    { layoutId: string; images: { id: string; order: number }[] },
    { rejectValue: string }
>('image/changeImageOrder', async ({ layoutId, images }, { rejectWithValue }) => {
    try {
        const data = {
            data: images.map((img, index) => ({
                type: 'images',
                id: img.id,
                meta: {
                    order: img.order,
                },
            })),
        };

        const res = await axiosInstance.patch(
            `/image_layouts/${layoutId}/relationships/images`,
            data,
        );
        const transformedData = res.data.data.relationships.images.data;
        console.log('transformedData', transformedData);
        return {
            layoutId: layoutId,
            images: transformedData,
        };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || error.message || 'Unknown error');
    }
});

export const deleteImageLayout = createAsyncThunk<
    void,
    { imageProjectId: string; layoutId: string },
    { rejectValue: string }
>('image/deleteImageLayout', async ({ imageProjectId, layoutId }, { rejectWithValue }) => {
    try {
        await axiosInstance.delete(`/image_projects/${imageProjectId}/layouts/${layoutId}`);
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || error.message || 'Unknown error');
    }
});

export const deleteImageFromLayout = createAsyncThunk<
    { layoutId: string; imageId: string },
    { layoutId: string; imageId: string },
    { rejectValue: string }
>('images/deleteImageFromLayout', async ({ layoutId, imageId }, { rejectWithValue }) => {
    try {
        const body = {
            data: [
                {
                    type: 'images',
                    id: imageId,
                },
            ],
        };
        const response = await axiosInstance.delete(
            `/image_layouts/${layoutId}/relationships/images`,
            { data: body },
        );
        return {
            layoutId,
            imageId,
        };
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(
                error.response.data.message || 'Failed to delete image from layout',
            );
        }
        return rejectWithValue('An unexpected error occurred while deleting the image from layout');
    }
});
